import { inject, Injectable } from '@angular/core';
import { QueryClient } from '@tanstack/angular-query-experimental';
import { EnrollhereApiService } from '../enrollhere-api/enrollhere-api';
import { log } from '../utils/logger';
import { CustomDispositionResult } from './disposition.service';

export interface TaskOutcomeCode {
  code: string;
  label: string;
  description?: string;
  rollup?: string;
  result?: string;
  resultCategory?: string;
  customResult?: CustomDispositionResult;
}

export interface TaskOutcomeList {
  id: string;
  agencyId: string;
  name: string;
  description?: string;
  codes: TaskOutcomeCode[];
  isTemplate: boolean;
}

export type TaskOutcomeSearchRequest = {
  filter?: { agencyId?: string[] };
  limit?: number;
};

export type TaskOutcomeSearchResult = {
  outcomeLists: TaskOutcomeList[];
  total: number;
  limit: number;
};

const logger = log.getLogger('task-outcome.service');

@Injectable({ providedIn: 'root' })
export class TaskOutcomeService {
  private apiService = inject(EnrollhereApiService);
  private queryClient = inject(QueryClient);

  async listOutcomeLists(
    agencyId: string
  ): Promise<{ outcomeLists: TaskOutcomeList[]; total: number }> {
    try {
      return await this.apiService.get<{
        outcomeLists: TaskOutcomeList[];
        total: number;
      }>(`task-outcome?agencyId=${encodeURIComponent(agencyId)}`);
    } catch (error) {
      logger.error('Error listing outcome lists', { agencyId, error });
      return { outcomeLists: [], total: 0 };
    }
  }

  /**
   * Aggregate outcome lists across entitled agencies.
   * Backend: POST /v1/task-outcome/search
   */
  async searchOutcomeLists(
    request: TaskOutcomeSearchRequest
  ): Promise<TaskOutcomeSearchResult> {
    return this.apiService.post<TaskOutcomeSearchResult>(
      'task-outcome/search',
      request
    );
  }

  async getOutcomeList(
    agencyId: string,
    listId: string
  ): Promise<TaskOutcomeList | null> {
    try {
      const response = await this.apiService.get<{
        outcomeList: TaskOutcomeList;
      }>(
        `task-outcome/${encodeURIComponent(listId)}?agencyId=${encodeURIComponent(agencyId)}`
      );
      return response.outcomeList;
    } catch (error) {
      logger.error('Error fetching outcome list', { agencyId, listId, error });
      return null;
    }
  }

  async createOutcomeList(
    agencyId: string,
    payload: {
      name: string;
      description?: string | null;
      codes: TaskOutcomeCode[];
    }
  ): Promise<TaskOutcomeList> {
    const response = await this.apiService.post<{
      outcomeList: TaskOutcomeList;
    }>('task-outcome', { ...payload, agencyId });
    return response.outcomeList;
  }

  async updateOutcomeList(
    agencyId: string,
    listId: string,
    payload: {
      name?: string;
      description?: string | null;
      codes?: TaskOutcomeCode[];
    }
  ): Promise<TaskOutcomeList> {
    const response = await this.apiService.put<{
      outcomeList: TaskOutcomeList;
    }>(
      `task-outcome/${encodeURIComponent(listId)}?agencyId=${encodeURIComponent(agencyId)}`,
      payload
    );
    return response.outcomeList;
  }

  async deleteOutcomeList(
    agencyId: string,
    listId: string
  ): Promise<{ categoryCount: number }> {
    return this.apiService.delete<{ categoryCount: number }>(
      `task-outcome/${encodeURIComponent(listId)}?agencyId=${encodeURIComponent(agencyId)}`
    );
  }

  async getTemplates(): Promise<TaskOutcomeList[]> {
    const response = await this.apiService.get<{
      templates: TaskOutcomeList[];
    }>('task-outcome/templates');
    return response.templates;
  }

  async createFromTemplate(
    agencyId: string,
    templateId: string
  ): Promise<TaskOutcomeList> {
    const response = await this.apiService.post<{
      outcomeList: TaskOutcomeList;
    }>('task-outcome/from-template', { agencyId, templateId });
    return response.outcomeList;
  }

  invalidateQueries() {
    this.queryClient.invalidateQueries({ queryKey: ['taskOutcomeLists'] });
    this.queryClient.invalidateQueries({ queryKey: ['taskOutcomeTemplates'] });
  }
}
